import React, { useCallback, useMemo, useState } from "react";

// import qs from "query-string";
import some from "lodash/some";

import { Trans } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useNavigate,
  useParams,
  // useLocation
} from "react-router-dom";

import { client } from "../../bit/board/hooks/useGraphQLClient";

import Modal from "../../components/Modal";

import { useClientPlan } from "../../lib/hooks/useQueryBoard";

import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";

// import useEventTracking from "../../bit/tracking/useEventTracking";

import { MUTATION_ACCESS_SCAN } from "../../config/graphql/mutation";
import { QUERY_ACCESS_EVENT } from "../../config/graphql/query";

import AggrementDialog from "../../components/Aggrement/AggrementDialog";
import { useAgreementState } from "../../components/Aggrement/hooks/useAgreementState";

const Scanning = React.memo(() => {
  const navigate = useNavigate();
  // const { search } = useLocation();

  const { code, action } = useParams();

  // const query = useMemo(() => qs.parse(search), [search]);

  // const qr = query.qr ? "qr_" : "";

  const clientPlan = useClientPlan();

  const queryClient = useQueryClient();

  // const { trackEvent } = useEventTracking();

  const { screen, unacceptedAgreements } = useAgreementState();

  const { data, status: requestStatus } = useQuery(
    [`scan/${code}`],
    async () => {
      let input: any = {
        code,
      };

      if (action) {
        input = {
          ...input,
          action: action === "checkout" ? "checkOut" : "checkIn",
        };
      }

      if (
        Array.isArray(unacceptedAgreements) &&
        unacceptedAgreements.length > 0
      ) {
        input = {
          ...input,
          acceptedAgreements: unacceptedAgreements.map(
            (agreement) => agreement.id,
          ),
          screen,
        };
      }

      const response = await client.request<{ accessScan: IAccessEvent }>(
        MUTATION_ACCESS_SCAN,
        {
          input,
        },
      );

      const accessScanId = response?.accessScan?.id;

      /**
       * Prefetch access event so we don't wait when on contact page
       */
      await queryClient
        .prefetchQuery([`access/event/${accessScanId}`], () =>
          client.request(QUERY_ACCESS_EVENT, { id: accessScanId }),
        )
        .catch(() => null);

      return response;
    },
    {
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess() {
        // trackEvent({
        //   type: `${qr}code_scan`,
        //   value: `${data?.accessScan?.type
        //     ?.replace(/([a-z])([A-Z])/, "$1_$2")
        //     .toLowerCase()}_${action || "checkin"}_success`,
        // });
      },
      onError() {
        // trackEvent({
        //   type: `${qr}code_scan`,
        //   value: `${action || "checkin"}_error`,
        // });
      },
    },
  );

  const status = useDebounceStatus(requestStatus, 1500);

  const id = data?.accessScan?.id;
  const type = data?.accessScan?.type;
  const metadata = data?.accessScan?.metadata;
  const fullName = data?.accessScan?.owner?.fullName;

  const receiver = data?.accessScan?.receiver;

  const autoEmail =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.email;
  const autoSMS =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.sms;
  const autoCall =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.call;
  const autoPing =
    receiver?.availablePreferences?.meetingAttendee?.autoChannel?.ping;

  const direction = useMemo(() => {
    if (data) {
      return !!(metadata && "checkOut" in metadata) ? "checkout" : "checkin";
    }

    return action || "checkin";
  }, [data]);

  const translationKey = useMemo(() => {
    const key = `check:status.${status}`;

    if (!(status === "success" && type)) {
      return key;
    }

    if (direction === "checkout" || type !== "MeetingAttendee") {
      return `${key}.${direction}.${type}`;
    }

    let messageType = "no-email-sms";

    if (autoCall || autoPing) {
      messageType = "call-ping";
    } else if (autoEmail || autoSMS) {
      messageType = "email-sms";
    }

    return `${key}.${direction}.${type}.${messageType}`;
  }, [status, direction, type, autoCall, autoPing, autoEmail, autoSMS]);

  const onDismiss = useCallback(() => {
    /**
     * If attendee employee check in successfully,
     * redirect him to the contact screen
     */
    const isFree = clientPlan && clientPlan === "free";

    /**
     * Can the user be navigated to contact page based on permissions and cations
     *
     * - Has to be non free user
     * - Access must belong to a visitor that is checking in
     * - Employee has to have a phone number set
     * - Employee has to have at least one notification channel enabled
     */

    const canContinue =
      !isFree &&
      id &&
      direction === "checkin" &&
      status === "success" &&
      type === "MeetingAttendee" &&
      receiver?.phone;

    if (canContinue) {
      if (autoCall || autoPing) {
        return navigate(
          `/access/checkin/contact/${id}/${!!autoCall ? "call" : "ping"}`,
          {
            replace: true,
          },
        );
      }

      // if (!!autoSMS) {
      //   return navigate("/", { replace: true });
      // }

      if (
        some(
          receiver?.availablePreferences?.meetingAttendee?.channel,
          (v) => v === true,
        )
      ) {
        return navigate(`/contact/${id}`, { replace: true });
      }
    }

    /**
     * In case there was an error, try to redirect the user to where he came from in order to retry the action
     */
    if (status === "error") {
      return navigate(-1);
    }

    return navigate("/");
  }, [id, direction, status, type, action]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 3000;
    }

    if (status === "success") {
      return 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  return (
    <Modal
      title={
        // @ts-expect-error
        <Trans i18nKey={`${translationKey}.title`} values={{ fullName }} />
      }
      message={
        // @ts-expect-error
        <Trans i18nKey={`${translationKey}.message`} values={{ fullName }} />
      }
      status={status}
      onDismiss={onDismiss}
    />
  );
});

const ScanningWithAgreement = () => {
  const navigate = useNavigate();

  const { unacceptedAgreements, showFirst } = useAgreementState();

  // prettier-ignore
  const [pendingAgreements, setPendingAgreements] = useState<IEntranceAgreementFile[]>(!showFirst && unacceptedAgreements ? unacceptedAgreements: []);

  if (!!(Array.isArray(pendingAgreements) && pendingAgreements.length > 0)) {
    return (
      <AggrementDialog
        aggrements={pendingAgreements}
        onDismiss={() => {
          navigate("/");
        }}
        onSubmit={() => {
          setPendingAgreements((prev) => {
            const [_accepted, ...rest] = prev;

            return rest;
          });
        }}
      />
    );
  }

  return <Scanning />;
};

export default ScanningWithAgreement;
